import {
  DataType,
  Form,
  FormOptions,
  Section,
  SelectOption,
  useNotify
} from '@globaleris/ui.library'
import { Box, Breadcrumbs, Paper } from '@material-ui/core'
import moment from 'moment'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useHistory } from 'react-router-dom'
import { EnumCommodity, EnumGrupoDocumento, EnumTipoPeaje } from '../../api'
import { InvoiceResponseModel } from '../../api/Argentia/endpoints/invoices/model/invoices'
import { NewProcedure, Procedure } from '../../api/Argentia/endpoints/procedures'
import { AnnexedParameters, ProcedureStates, ProcedureTypes } from '../../api/Argentia/enums'
import {
  ActivityTypeModel,
  CapacityModel,
  ContractModel,
  TollModel
} from '../../api/Argentia/model/contracts'
import { EnumPresionTension } from '../../api/oData/enums/enumPresionTension'
import { ErrorDetailList } from '../../components/errorDetailList/errorDetailList'
import Page, { PageLayout } from '../../components/page/page'
import PageHeader from '../../components/page/pageHeader'
import { BreadcrumbLink } from '../../components/shared/breadcrumbLink/breadcrumbLink'
import {
  AttachFileDialog,
  AttachFileDialogRef
} from '../../components/shared/documents/attachFileDialog'
import {
  enumEnumToValueNumber,
  enumKeyToValueString,
  enumToSelectOptionArray,
  enumToSelectOptionArrayDefaultValues,
  generateUniqueKeyWithNoPrefix
} from '../../helpers'
import { missingFieldsFormWithRequiredText } from '../../helpers/validateFormHelper'
import { useConfirmationDialog, useErrorDetailListDialog, useFormDialog } from '../../hooks'
import { GlobalNotification, NonAuthRoutes } from '../../model'
import { InformationDialogs } from '../../model/enums/informationDialogs'
import { fetchClientContractsByID, getCustomerDomain, notifyError } from '../../redux'
import {
  selectClientContracts,
  selectClientData,
  selectIsFetchingClientContracts
} from '../../redux/selectors/client.selectors'
import { getLinkClientText } from '../clients/client.helper'
import { ClientFileModel, SubjectModel } from '../clients/clients.model'
import {
  createProcedure,
  fetchProcedureItem,
  fetchProceduresListItemsByClient
} from '../comunicationCenter/tabs/procedures/comunicationProdecures.service'
import { listCnaeItemMapper, listTollItemMapper } from '../contracts/contracts.mapper'
import { EnumContractCapacitiesOptions } from '../contracts/contracts.model'
import {
  fetchActivityTypes,
  fetchContractsBySubjectID,
  fetchTolls,
  getContractDetails,
  revertChangeOwner
} from '../contracts/contracts.service'
import { fetchDocumentsTypesByDocumentGroup } from '../documents/documents.service'
import { fetchInvoiceDetails } from '../invoicesPage/invoicesPage.service'
import { AttachedFilesTable } from './components/attachedFilesTable/attachedFilesTable'
import {
  CHANGE_REASONS_ADM_CHANGE_PWR,
  CHANGE_REASONS_ADM_CHANGE_PWR_ALL,
  CHANGE_REASONS_TEC_CHANGE_GAS,
  CHANGE_REASONS_UPDATE_OWNER_PWR,
  filterBySubComplaintType,
  GAS_UPDATE_OWNER_VALUES,
  getChangeReasonsValuesGas,
  getComplaintSubTypeByCategory,
  PRINCIPAL_RESIDENCE_ADM_CHANGE_PWR,
  UPDATE_OWNER_CHANGE_REASONS_PWR,
  VALID_TOLLTYPE
} from './procedures.helper'
import {
  atrModelToFormModelMapper,
  capacityModelToFormModelMapper,
  capacityModelToSelectOptionMapper,
  contractModelToProcedureFormModelMapper,
  cupsModelToProcedureFormModelMapper,
  getParameters,
  getProcedureTypeEnumByMode,
  getProcedureTypeOptions,
  getProcessTypeEnumByMode,
  getReasonOptionsByPressure,
  mapAnnexedParam,
  mapAttachedDocumentFilesToDocuments,
  mapContractsToSelectOptions
} from './procedures.mapper'
import { fetchAnnexedParams } from './procedures.service'
import {
  AttachedDocumentFile,
  EnumAdministrativeRequest,
  EnumComplaintOwnerType,
  EnumComplaintType,
  EnumComplaintTypeInvoice,
  EnumComplaintTypeProcedure,
  EnumDeviceTransfer,
  EnumEffectiveDate,
  EnumEffectiveDatePower,
  EnumGasStationType,
  EnumGasUsage,
  EnumGetAnnexedParamsType,
  EnumMeterProperty,
  EnumProcedureProcess,
  EnumProcedureReasonChangeOwnerGas,
  EnumProcedureReasonChangeOwnerPower,
  EnumProcedureType,
  EnumProcedureTypeGas,
  EnumProcedureTypeHidden,
  EnumProcedureTypePower,
  EnumReasonTermination,
  EnumReasonTerminationGas,
  EnumReasonTerminationPower,
  EnumRequestType,
  EnumResidential,
  EnumTension,
  ProcedureFormData,
  ProcedureFormMode,
  ProcedureFormModeType
} from './procedures.types'
import { EnumParamAnexoValues } from '../../api/Argentia/enums/enumParamAnexoValues'
import { ConfirmationDialogs } from '../../model/enums/confirmationDialogs'

interface AddProcedurePageProps {
  location?: {
    state: {
      contractId?: number
      oldContractId?: number
      cupsCode?: string
      capacityNewStartDate?: Date
      invoiceId?: number
      procedureId?: number
      capacityCode?: string
      formMode: ProcedureFormModeType
      newDate?: Date
      clientData?: ClientFileModel
      admChangeReasons?: EnumContractCapacitiesOptions
    }
  }
}

const defaultState = {
  contractId: null,
  oldContractId: null,
  cupsCode: undefined,
  capacityNewStartDate: undefined,
  invoiceId: undefined,
  procedureId: undefined,
  formMode: ProcedureFormMode.GENERAL as ProcedureFormModeType,
  capacityCode: undefined,
  newDate: moment().toDate(),
  admChangeReasons: undefined
}

const MAX_CHAR_COMMENTS = 450

export const AddProcedurePage: React.FC<AddProcedurePageProps> = ({ location }) => {
  const [t] = useTranslation(['procedures', 'globalerisLibrary', 'enums'])
  const [globalNotification, setGlobalNotification] = useState<GlobalNotification | undefined>(
    undefined
  )
  const history = useHistory()
  const { info, error } = useNotify()
  const { showErrorDetailListDialog, errorDetailListDialogProps } = useErrorDetailListDialog()
  const { getInformation, closeInformation, getConfirmation, closeConfirmation } =
    useConfirmationDialog()
  const { openForm, updateFormDefinition, closeForm, formProps, CustomFormDialog, setFormWaiting } =
    useFormDialog()

  const {
    contractId,
    oldContractId,
    cupsCode,
    capacityNewStartDate,
    invoiceId,
    capacityCode,
    procedureId,
    formMode,
    newDate,
    admChangeReasons
  } = location?.state ?? defaultState

  const clientData = location?.state?.clientData
    ? Array.isArray(location?.state?.clientData)
      ? location?.state?.clientData[0]
      : location?.state?.clientData
    : useSelector(selectClientData)

  const contracts = useSelector(selectClientContracts)
  const isFetchingContracts = useSelector(selectIsFetchingClientContracts)

  const [contractsOptions, setContractsOptions] = useState<SelectOption[]>([])

  const [documentsTypes, setDocumentsTypes] = useState<SelectOption[]>([])
  const [showAttachmentDialog, setShowAttachmentDialog] = useState<boolean>(false)

  const [contractDetails, setContractDetails] = useState<ContractModel | undefined>(undefined)
  const [invoiceDetails, setInvoiceDetails] = useState<InvoiceResponseModel>()
  const [procedureDetails, setProcedureDetails] = useState<Procedure>()
  const [hasValidReopeningParameter, setHasValidReopeningParameter] = useState<boolean>(false)
  const [isNonPayment, setIsNonPayment] = useState<boolean>(false)
  const [remainingChars, setRemainingChars] = useState<number>(MAX_CHAR_COMMENTS)
  const maxChar = MAX_CHAR_COMMENTS

  const isChangeOwnership =
    formMode === ProcedureFormMode.CAMBIO_TITULAR_GAS ||
    formMode === ProcedureFormMode.CAMBIO_TITULAR_ADM_TEC_GAS ||
    formMode === ProcedureFormMode.CAMBIO_TITULAR_POWER ||
    formMode === ProcedureFormMode.CAMBIO_TITULAR_ADM_TEC_POWER

  const [formData, setFormData] = useState<ProcedureFormData>({
    context: getProcessTypeEnumByMode(formMode),
    type: getProcedureTypeEnumByMode(formMode),
    newDate: isChangeOwnership ? newDate : undefined,

    conditionalRegistration: false,
    subrogation: false,
    principalResidence: false,
    socialSubsidy: false,

    qaEstimated: 0,
    qdHired: 0
  })

  const [fixedMode, setFixedMode] = useState<boolean>(
    isChangeOwnership ||
      formMode === ProcedureFormMode.BAJA_CONTRATO_GAS ||
      formMode === ProcedureFormMode.BAJA_CONTRATO_POWER ||
      formMode === ProcedureFormMode.RECLAMACION_FACTURA ||
      formMode === ProcedureFormMode.RECLAMACION_TRAMITACION ||
      formMode === ProcedureFormMode.ALTA_CAPACIDAD ||
      formMode === ProcedureFormMode.MODIFICACION_TECNICA ||
      formMode === ProcedureFormMode.SUSPENSION_CONTRATO_GAS ||
      formMode === ProcedureFormMode.SUSPENSION_CONTRATO_POWER ||
      formMode === ProcedureFormMode.RENGANCHE_CONTRATO_GAS
  )
  const [isSaving, setIsSaving] = useState<boolean>(false)
  const [isFetchingTolls, setIsFetchingTolls] = useState<boolean>(false)
  const [isFetchingCnae, setIsFetchingCnae] = useState<boolean>(false)
  const [isFetchingContract, setIsFetchingContract] = useState<boolean>(false)
  const [isFetchingComplaintTypeList, setIsFetchingComplaintTypeList] = useState<boolean>(false)
  const [isFetchingComplaintSubTypeList, setIsFetchingComplaintSubTypeList] =
    useState<boolean>(false)
  const [isFetchingChangeReasonsList, setIsFetchingChangeReasonsList] = useState<boolean>(false)
  const [isFetchingEssentialIndicatorList, setIsFetchingEssentialIndicatorList] =
    useState<boolean>(false)
  const [isFetchingSuspensionGasList, setIsFetchingSuspensionGasList] = useState<boolean>(false)
  const [isFetchingAnnexedParameters, setIsFetchingAnnexedParameters] = useState<boolean>(false)
  const [cnaeList, setCnaeList] = useState<ActivityTypeModel[]>([])
  const [tollList, setTollList] = useState<TollModel[]>([])
  const [contractCapacities, setContractCapacities] = useState<SelectOption[]>([])
  const [complaintTypeList, setComplaintTypeList] = useState<SelectOption[]>([])
  const [subComplaintTypeList, setSubComplaintTypeList] = useState<SelectOption[]>([])
  const [changeReasonsListGas, setChangeReasonsListGas] = useState<SelectOption[]>([])
  const [changeReasonsListPwr, setChangeReasonsListPwr] = useState<SelectOption[]>([])
  const [filteredChangeReasonsList, setFilteredChangeReasonsList] = useState<SelectOption[]>([])
  const [billingFrequencyList, setbillingFrequencyList] = useState<SelectOption[]>([])
  const [essentialIndicatorList, setEssentialIndicatorList] = useState<SelectOption[]>([])
  const [protectedIndicatorList, setProtectedIndicatorList] = useState<SelectOption[]>([])
  const [complaintOwnerTypeList, setComplaintOwnerTypeList] = useState<SelectOption[]>([])
  const [contractConceptsList, setContractConceptsList] = useState<SelectOption[]>([])
  const [invoiceConceptsList, setInvoiceConceptsList] = useState<SelectOption[]>([])
  const [incidentCodeList, setIncidentCodeList] = useState<SelectOption[]>([])
  const [suspensionGasList, setSuspensionGasList] = useState<SelectOption[]>([])
  const [isHighPressure, setIsHighPressure] = useState<boolean>(false) // pressureGroupId !== EnumGrupoPresionTension.GrupoDistribucion3
  const [invalidContract, setInvalidContract] = useState<boolean>(false)
  const [missingCNAE, setMissingCNAE] = useState<boolean>(false)
  const [invalidDates, setInvalidDates] = useState<boolean>(false)
  const [factMethod, setFactMethod] = useState<string | undefined>(undefined)

  // Attach File Dialog
  const attachFileDialogRef = useRef<AttachFileDialogRef>(null)

  const onResetDialog = () => {
    attachFileDialogRef?.current && attachFileDialogRef?.current?.reset()
  }

  const [attachedDocumentFiles, setAttachedDocumentFiles] = useState<AttachedDocumentFile[]>([])

  useEffect(() => {
    const getActivityTypes = async () => {
      setIsFetchingCnae(true)
      await fetchActivityTypes().then(async response => {
        const CNAEList = response.data
        if (CNAEList !== undefined) {
          setCnaeList(CNAEList)
        }
        invoiceId && getInvoiceDetails(invoiceId, CNAEList)
        contractId && getDetails(contractId, CNAEList)
        procedureId && getProcedureDetails(procedureId, CNAEList)

        if (formMode === ProcedureFormMode.RENGANCHE_CONTRATO_GAS && procedureId) {
          let contracts = await fetchContractsBySubjectID(clientData?.id)

          const contractId = contracts.data?.find(p =>
            p.cupsList?.some(cup => cup.code === cupsCode)
          )?.id

          getDetails(contractId!, CNAEList)
        }
      })
      setIsFetchingCnae(false)
    }

    getActivityTypes()

    if (clientData?.id && !contracts && !invoiceId && !procedureId && !contractId)
      fetchClientContractsByID(clientData.id)
  }, [])

  useEffect(() => {
    const commodity = contractDetails?.commodity || formData?.commodity
    if (commodity) {
      const getDocumentsTypesByDocumentGroup = async (documentGroupId: number) => {
        // setFormIsLoading(true)
        await fetchDocumentsTypesByDocumentGroup(documentGroupId).then(response => {
          if (response.success && response.data != undefined) {
            setDocumentsTypes(response.data)
            // setFormIsLoading(false)
          } else {
            error(t('documents:notifications.getDocuments.error'))
          }
        })
      }

      commodity === EnumCommodity.GasNatural
        ? getDocumentsTypesByDocumentGroup(EnumGrupoDocumento.DocumentosAdjuntosGas)
        : getDocumentsTypesByDocumentGroup(EnumGrupoDocumento.DocumentosAdjuntosElectricidad)
    }
  }, [contractDetails, formData?.commodity])

  useEffect(() => {
    contractsList()
  }, [contracts])

  useEffect(() => {
    const getTolls = async (commodityType: EnumCommodity) => {
      setIsFetchingTolls(true)
      await fetchTolls(commodityType).then(response => {
        if (response.data !== undefined) {
          setTollList(response.data)
        }
      })
      setIsFetchingTolls(false)
    }

    contractDetails?.commodity && getTolls(contractDetails?.commodity)
  }, [contractDetails])

  const contractsList = useCallback(() => {
    setContractsOptions(mapContractsToSelectOptions(contracts ?? []))
  }, [contracts])

  const showContractErrorMessage = async (
    reason: 'cnae' | 'tolls' | 'cupsMissing' | 'consumption' | 'qaMissing'
  ) => {
    await getInformation(
      InformationDialogs.PROCEDURE_CONTRACT_NOT_VALID,
      t('informationDialogs:PROCEDURE_CONTRACT_NOT_VALID.message', {
        reason: t(`informationDialogs:PROCEDURE_CONTRACT_NOT_VALID.reasons.${reason}`)
      })
    ).then(res => closeInformation())
  }

  const showMissingProductsErrorMessage = async () => {
    await getInformation(
      InformationDialogs.PROCEDURE_PRODUCTS_MISSING,
      t('informationDialogs:PROCEDURE_PRODUCTS_MISSING.message')
    ).then(res => closeInformation())
  }

  const showCnaeErrorMessage = async () => {
    await getInformation(
      InformationDialogs.PROCEDURE_CNAE_MISSING,
      t('informationDialogs:PROCEDURE_CNAE_MISSING.message')
    ).then(res => closeInformation())
  }

  const checkPrincipalResidenceForAdministrativeChangePwr =
    (formData.type === EnumProcedureType.ADMINISTRATIVE_CHANGE_PWR ||
      formData.type === EnumProcedureType.ADMINISTRATIVE_TECHNICAL_CHANGE_PWR) &&
    PRINCIPAL_RESIDENCE_ADM_CHANGE_PWR.includes(formData.changeReason as any)

  const isElectricityUpdateOwner =
    formData.commodity === EnumCommodity.Electricidad &&
    (formData.type === EnumProcedureType.ADMINISTRATIVE_CHANGE_PWR ||
      formData.type === EnumProcedureType.ADMINISTRATIVE_TECHNICAL_CHANGE_PWR) &&
    UPDATE_OWNER_CHANGE_REASONS_PWR.includes(formData.changeReason as any)

  const isCNAERequiredForGas =
    ((formData.type === EnumProcedureType.NEW_WITH_CHANGES ||
      formData.type === EnumProcedureType.ADMINISTRATIVE_CHANGE ||
      formData.type === EnumProcedureType.ADMINISTRATIVE_TECHNICAL_CHANGE_GAS) &&
      formData.admChangeReasons === '28') ||
    (GAS_UPDATE_OWNER_VALUES.includes(formData.admChangeReasons as any) &&
      (factMethod === EnumParamAnexoValues.FACTURACION_CAPACIDAD_DEMANDA ||
        VALID_TOLLTYPE.includes(formData.toll as any)))

  const hasEssentialIndicatorGas =
    formData.essentialIndicatorGas !== undefined && formData.essentialIndicatorGas !== null

  const checkCnae = (cnae?: string) => {
    // CNAE is needed for POWER contracts and "With Changes" => 'Administrative' or 'BOTH' requests
    const contractCnae = cnae ?? formData.cnaeCode
    const hasCNAE = contractCnae && contractCnae.length > 0

    if (
      !hasCNAE &&
      (
        formData.type === EnumProcedureType.NEW_SUPPLY)
    ) {
      setMissingCNAE(true)
      showCnaeErrorMessage()
      return
    }
    setMissingCNAE(false)
  }

  const checkQaIsMissing = (foundCapacity: CapacityModel | undefined) => {
    if (
      formData.type === EnumProcedureTypeGas.NEW_CAPACITY &&
      foundCapacity != undefined &&
      (foundCapacity.qaEstimada === undefined || foundCapacity.qaEstimada === 0)
    ) {
      setInvalidContract(true)
      showContractErrorMessage('qaMissing')
    }
  }

  const mapCupsData = (entity: InvoiceResponseModel | undefined, cnaeList) => {
    if (entity) {
      let cupsData = {} as Partial<ProcedureFormData>

      if (entity.cups) {
        const cups = entity.cupsModel
        cupsData = { ...cupsData, ...cupsModelToProcedureFormModelMapper(cups) }
        const cnaeId = cnaeList.find(cnae => cnae.code === cupsData.cnaeCode)?.id
        cupsData = { ...cupsData, cnaeId }
      } else {
        // error no hay cups asociado al contrato
        showContractErrorMessage('cupsMissing')
      }
      checkCnae(cupsData?.cnaeCode)
      return cupsData
    }
  }

  const getDetails = async (contractID: number, cnaeList: ActivityTypeModel[]) => {
    // setIsFetching(true)
    setIsFetchingContract(true)
    setInvalidContract(false)
    const response = await getContractDetails(contractID)
    if (response.success && response.data) {
      setIsFetchingContract(false)
      const contract = contractModelToProcedureFormModelMapper(response.data)
      setContractDetails(contract)

      // if procedure come from change owner or technical modification component,
      // map the data from the response so readonly contract select can display it
      if (isChangeOwnership || formMode === ProcedureFormMode.MODIFICACION_TECNICA) {
        setContractsOptions(mapContractsToSelectOptions([response.data]))
      }

      let cupsData = {} as Partial<ProcedureFormData>
      let capacityId: number | undefined
      let foundCapacity: CapacityModel | undefined
      if (contract.capacities && contract.capacities.length > 0) {
        // checkCurrentCapacities(contract.capacities) -> took out validation as it is possible to have multiple toll within capacities (US 53331)
        cupsData = capacityModelToFormModelMapper(contract.capacities[0])
        setContractCapacities(capacityModelToSelectOptionMapper(contract.capacities))
        foundCapacity = contract.capacities.find(cap => cap.code === capacityCode)
        capacityId = foundCapacity?.id
        if (foundCapacity) {
          cupsData = capacityModelToFormModelMapper(foundCapacity)
        } else {
          cupsData = capacityModelToFormModelMapper(contract.capacities[0])
        }
      } else if (contract.contractATR) {
        cupsData = atrModelToFormModelMapper(contract.contractATR)
      } else {
        setInvalidContract(true)
        showMissingProductsErrorMessage()
      }
      if (
        contract.commodity === EnumCommodity.GasNatural &&
        (cupsData.toll === undefined ||
          cupsData.qdHired === undefined ||
          cupsData.qaEstimated === undefined)
      ) {
        setInvalidContract(true)
      } else {
        setInvalidContract(false)
      }
      checkQaIsMissing(foundCapacity)

      if (contract.cupsList && contract.cupsList.length > 0) {
        const cups = contract.cupsList[0]
        const cupsIsHighPressure =
          cups.pressureGroupId === EnumPresionTension.NP1 ||
          cups.pressureGroupId === EnumPresionTension.NP2
            ? true
            : cups.pressureGroupId !== EnumPresionTension.NP0
              ? true
              : false
        setIsHighPressure(cupsIsHighPressure)
        cupsData = { ...cupsData, ...cupsModelToProcedureFormModelMapper(cups) }
        const cnaeId = cnaeList.find(cnae => cnae.code === cupsData.cnaeCode)?.id
        cupsData = { ...cupsData, cnaeId }
      } else {
        // error no hay cups asociado al contrato
        setInvalidContract(true)
        showContractErrorMessage('cupsMissing')
      }

      setFactMethod(
        contract.annexedContracts?.find(
          x =>
            enumEnumToValueNumber(AnnexedParameters, x.annexedParam) ===
            AnnexedParameters.METODO_FACTURACION
        )?.value
      )

      checkCnae(cupsData?.cnaeCode)

      // if procedure come from capacityAddPage component under ClientFilePage (modification request from product capacity) -> AnnexedParam needs to be loaded with params list SelectOption
      admChangeReasons &&
        getAnnexedParams(
          formData,
          EnumGetAnnexedParamsType.TECHNICAL_CHANGE_GAS,
          AnnexedParameters.MOTIVO_MODIFICACION
        )
      // if procedure come form suspension contract under ClientFilePage -> AnnexedParam needs to be loaded with params list SelectOption
      if (
        formMode === ProcedureFormMode.SUSPENSION_CONTRATO_GAS ||
        formMode === ProcedureFormMode.RENGANCHE_CONTRATO_GAS
      ) {
        getAnnexedParams(formData, EnumGetAnnexedParamsType.ACTION_GAS)
      }

      if (formMode === ProcedureFormMode.CAMBIO_TITULAR_ADM_TEC_GAS) {
        getAnnexedParams(
          formData,
          EnumGetAnnexedParamsType.UPDATE_OWNER_ADM_TEC_GAS,
          AnnexedParameters.MOTIVO_MODIFICACION
        )
      }

      if (formMode === ProcedureFormMode.CAMBIO_TITULAR_GAS) {
        getAnnexedParams(
          formData,
          EnumGetAnnexedParamsType.UPDATE_OWNER_GAS,
          AnnexedParameters.MOTIVO_MODIFICACION
        )
      }

      if (contract.commodity === EnumCommodity.Electricidad && isChangeOwnership) {
        getAnnexedParams(
          formData,
          EnumGetAnnexedParamsType.UPDATE_OWNER_PWR,
          AnnexedParameters.MOTIVO_MODIFICACION
        )
      }

      setFormData(prevState => {
        const newFormData = {
          newDate: prevState.newDate,
          contractId: contractID,
          context: prevState.context,
          type: prevState.type,
          conditionalRegistration: prevState.conditionalRegistration,
          subrogation: prevState.subrogation,
          principalResidence: prevState.principalResidence,
          originProcedureId: procedureId,
          ...cupsData,
          capacityId: capacityId,
          commodity: contract.commodity,
          ...(formData.type === EnumProcedureTypeHidden.TERMINATION && {
            reasonTermination: EnumReasonTermination.UNDEFINED_DATE
          }),
          ...(formData.context === EnumProcedureProcess.SUSPENSION_CONTRATO_POWER && {
            reasonTermination: EnumReasonTermination.SERVICE_INTERRUPTION
          }),
          ...(formData.context === EnumProcedureProcess.SUSPENSION_CONTRATO_GAS && {
            reasonGasSuspension: 'A10011' // corresponde a -> Cierre por falta de pago
          }),
          ...(formData.context === EnumProcedureProcess.REENGANCHE_CONTRATO_GAS && {
            reasonGasSuspension: 'A10002' // corresponde a -> Renganche del punto suministro
          }),
          ...(admChangeReasons && { admChangeReasons: admChangeReasons?.toString() }),
          ...((isChangeOwnership ||
            formData.type === EnumProcedureType.NEW_CAPACITY ||
            formData.type === EnumProcedureTypeHidden.TECHNICAL_CHANGE ||
            formData.type === EnumProcedureTypeHidden.TERMINATION ||
            formData.type === EnumProcedureTypePower.SUSPEND_SUPPLY_PWR) && {
            efectiveDate: EnumEffectiveDate.SPECIFIC_DATE
          }),
          ...(capacityNewStartDate && { date: capacityNewStartDate })
        }

        return newFormData
      })
    } else {
      error(t('procedures:notifications.errors.apiContract'))
      history.goBack()
    }
    // setIsFetching(false)
  }

  const getInvoiceDetails = async (invoiceId: number, cnaeList) => {
    const response = await fetchInvoiceDetails(invoiceId)
    if (response.success && response.data) {
      const invoice = response.data
      setInvoiceDetails(invoice)
      const cupsData = mapCupsData(invoice, cnaeList)
      setFormData(prevState => {
        const newFormData: ProcedureFormData = {
          newDate: prevState.newDate,
          invoiceId: invoiceId,
          context:
            cupsData?.commodity === EnumCommodity.GasNatural
              ? EnumProcedureProcess.RECLAMACION_FACTURA_GAS
              : EnumProcedureProcess.RECLAMACION_FACTURA_POWER,
          type:
            cupsData?.commodity === EnumCommodity.GasNatural
              ? EnumComplaintTypeInvoice.COMPLAINT_INVOICE_GAS
              : EnumComplaintTypeInvoice.COMPLAINT_INVOICE_PWR,
          invoiceCode: invoice.code,
          ...cupsData
        }
        getAnnexedParams(newFormData, EnumGetAnnexedParamsType.COMPLAINT_TYPE)
        return newFormData
      })
    } else {
      error(t('procedures:notifications.errors.apiInvoice'))
      history.goBack()
    }
  }

  const getProcedureDetails = async (procedureId: number, cnaeList) => {
    const response = await fetchProcedureItem(procedureId)
    if (response.success && response.data) {
      const procedure = response.data
      setProcedureDetails(procedure)
      const cupsData = mapCupsData(procedure, cnaeList)
      setFormData(prevState => {
        const newFormData: ProcedureFormData = {
          newDate: prevState.newDate,
          originProcedureId: procedureId,
          context:
            cupsData?.commodity === EnumCommodity.GasNatural &&
            formMode === ProcedureFormMode.RENGANCHE_CONTRATO_GAS
              ? EnumProcedureProcess.REENGANCHE_CONTRATO_GAS
              : cupsData?.commodity === EnumCommodity.GasNatural
                ? EnumProcedureProcess.RECLAMACION_TRAMITACION_GAS
                : EnumProcedureProcess.RECLAMACION_TRAMITACION_POWER,
          type:
            cupsData?.commodity === EnumCommodity.GasNatural &&
            formMode === ProcedureFormMode.RENGANCHE_CONTRATO_GAS
              ? EnumProcedureTypeGas.RECONNECT_SUPPLY_GAS
              : cupsData?.commodity === EnumCommodity.GasNatural
                ? EnumComplaintTypeProcedure.COMPLAINT_PROCEDURE_GAS
                : EnumComplaintTypeProcedure.COMPLAINT_PROCEDURE_PWR,
          procedureCode: procedure.code,
          ...cupsData
        }
        if (formMode !== ProcedureFormMode.RENGANCHE_CONTRATO_GAS) {
          getAnnexedParams(newFormData, EnumGetAnnexedParamsType.COMPLAINT_TYPE)
        }
        return newFormData
      })
    } else {
      error(t('procedures:notifications.errors.apiProcedure'))
      history.goBack()
    }
  }

  const checkAndSetValidReopeningParameter = async (): Promise<boolean> => {
    const response = await fetchProceduresListItemsByClient(clientData?.id)
    if (response.success && response.data !== undefined) {
      return response.data.some((item: any) => {
        return (
          item.state !== enumKeyToValueString(ProcedureStates, ProcedureStates.Rechazado) &&
          item.parameters?.some(
            (doc: any) =>
              doc.type ===
                enumKeyToValueString(AnnexedParameters, AnnexedParameters.GAS_TIPO_OPERACION) &&
              doc.value === 'A10011'
          )
        )
      })
    } else {
      return false
    }
  }

  const getAnnexedParams = async (
    formData: ProcedureFormData,
    fetchType: EnumGetAnnexedParamsType | null,
    paramId?: number
  ) => {
    let request: number[] = []
    let errorText: string = ''
    switch (fetchType) {
      case EnumGetAnnexedParamsType.COMPLAINT_TYPE: {
        if (formData?.commodity) {
          formData?.commodity === EnumCommodity.GasNatural
            ? (request = [
                EnumComplaintType.GAS,
                EnumComplaintOwnerType.GAS,
                AnnexedParameters.GAS_CONCEPTO_CONTRATACION, // Will be used in case subtype = 066
                AnnexedParameters.GAS_CONCEPTO_FACTURACION // Will be used in case subtype = 008
              ])
            : (request = [
                EnumComplaintType.PWR,
                EnumComplaintOwnerType.PWR,
                AnnexedParameters.PWR_CONCEPTO_CONTRATACION, // Will be used in case subtype = 066
                AnnexedParameters.PWR_CONCEPTO_FACTURACION, // Will be used in case subtype = 008
                AnnexedParameters.CODIGO_INCIDENCIA // Will be used in case subtype = 003
              ])
          setIsFetchingComplaintTypeList(true)
          errorText = t('procedures:forms.add.errors.ComplaintTypeNotFound')
        }
        break
      }
      case EnumGetAnnexedParamsType.SUBCOMPLAINT_TYPE: {
        setIsFetchingComplaintSubTypeList(true)
        request = [paramId!]
        errorText = t('procedures:forms.add.errors.ComplaintSubTypeNotFound')
        break
      }
      case EnumGetAnnexedParamsType.ADMINISTRATIVE_CHANGE_GAS:
      case EnumGetAnnexedParamsType.ADMINISTRATIVE_TECHNICAL_CHANGE_GAS:
      case EnumGetAnnexedParamsType.UPDATE_OWNER_ADM_TEC_GAS:
      case EnumGetAnnexedParamsType.UPDATE_OWNER_GAS: {
        // If we already have the static list, just filter it without fetching
        if (changeReasonsListGas.length > 0) {
          const filterValues = getChangeReasonsValuesGas(fetchType)

          const filteredChangeReasons = changeReasonsListGas.filter(item =>
            filterValues.includes(item.value as any)
          )
          setFilteredChangeReasonsList(filteredChangeReasons)
          setIsFetchingChangeReasonsList(false)
          return // Skip the fetch
        }
        setIsFetchingChangeReasonsList(true)
        request = [
          paramId!,
          AnnexedParameters.GAS_CLIENTE_ESENCIAL,
          AnnexedParameters.GAS_CLIENTE_PROTEGIDO
        ]
        errorText = t('procedures:forms.add.errors.admChangeReasonsNotFound')
        break
      }
      case EnumGetAnnexedParamsType.TECHNICAL_CHANGE_GAS: {
        // If we already have the static list, just filter it without fetching
        if (changeReasonsListGas.length > 0) {
          const filteredChangeReasons = changeReasonsListGas.filter(item =>
            CHANGE_REASONS_TEC_CHANGE_GAS.includes(item.value as any)
          )
          setFilteredChangeReasonsList(filteredChangeReasons)
          setIsFetchingChangeReasonsList(false)
          return // Skip the fetch
        }
        setIsFetchingChangeReasonsList(true)
        request = [paramId!]
        errorText = t('procedures:forms.add.errors.admChangeReasonsNotFound')
        break
      }
      case EnumGetAnnexedParamsType.NEW_WITHOUT_CHANGES: {
        if (formData?.commodity === EnumCommodity.GasNatural) {
          request = [
            AnnexedParameters.GAS_CLIENTE_ESENCIAL,
            AnnexedParameters.GAS_CLIENTE_PROTEGIDO
          ]
        } else {
          request = [AnnexedParameters.PWR_CLIENTE_ESENCIAL]
        }
        setIsFetchingEssentialIndicatorList(true)
        errorText = t('procedures:forms.add.errors.admChangeReasonsNotFound')
        break
      }
      case EnumGetAnnexedParamsType.NEW_WITH_CHANGES: {
        if (formData?.commodity === EnumCommodity.GasNatural) {
          request = [
            AnnexedParameters.GAS_CLIENTE_ESENCIAL,
            AnnexedParameters.GAS_CLIENTE_PROTEGIDO
          ]
        } else {
          request = [AnnexedParameters.PWR_CLIENTE_ESENCIAL]
        }
        setIsFetchingEssentialIndicatorList(true)
        errorText = t('procedures:forms.add.errors.admChangeReasonsNotFound')
        break
      }
      case EnumGetAnnexedParamsType.NEW_SUPPLY: {
        if (formData?.commodity === EnumCommodity.GasNatural) {
          request = [
            AnnexedParameters.GAS_CLIENTE_ESENCIAL,
            AnnexedParameters.GAS_CLIENTE_PROTEGIDO
          ]
        } else {
          request = [AnnexedParameters.PWR_CLIENTE_ESENCIAL]
        }
        setIsFetchingEssentialIndicatorList(true)
        errorText = t('procedures:forms.add.errors.admChangeReasonsNotFound')
        break
      }
      case EnumGetAnnexedParamsType.UPDATE_OWNER_PWR:
      case EnumGetAnnexedParamsType.ADMINISTRATIVE_CHANGE_PWR:
      case EnumGetAnnexedParamsType.ADMINISTRATIVE_TECHNICAL_CHANGE_PWR: {
        // If we already have the static list, just filter it without fetching
        if (changeReasonsListPwr.length > 0) {
          const filteredChangeReasons = changeReasonsListPwr.filter(item =>
            isChangeOwnership
              ? UPDATE_OWNER_CHANGE_REASONS_PWR.includes(item.value as any)
              : formMode === ProcedureFormMode.CAMBIO_CONTRATO_ATR
                ? CHANGE_REASONS_ADM_CHANGE_PWR.includes(item.value as any)
                : CHANGE_REASONS_ADM_CHANGE_PWR_ALL.includes(item.value as any)
          )
          setFilteredChangeReasonsList(filteredChangeReasons)
          setIsFetchingChangeReasonsList(false)
          return // Skip the fetch
        }
        request = [
          AnnexedParameters.MOTIVO_MODIFICACION_PWR,
          AnnexedParameters.PWR_CLIENTE_ESENCIAL,
          AnnexedParameters.PERIODICIDAD_FACTURACION
        ]
        setIsFetchingAnnexedParameters(true)
        break
      }
      case EnumGetAnnexedParamsType.ACTION_GAS: {
        request = [AnnexedParameters.GAS_TIPO_OPERACION]
        setIsFetchingSuspensionGasList(true)

        setHasValidReopeningParameter(await checkAndSetValidReopeningParameter())

        errorText = t('procedures:forms.add.errors.suspensionGasNotFound')
        break
      }
      default: {
        request = [paramId!]
        break
      }
    }
    await fetchAnnexedParams(request).then(response => {
      if (response.success) {
        switch (fetchType) {
          case EnumGetAnnexedParamsType.COMPLAINT_TYPE: {
            setComplaintTypeList(
              filterBySubComplaintType(
                mapAnnexedParam(request[0], response.data),
                formData?.type!,
                formData?.commodity!
              )
            )
            setComplaintOwnerTypeList(mapAnnexedParam(request[1], response.data))
            setContractConceptsList(mapAnnexedParam(request[2], response.data))
            setInvoiceConceptsList(mapAnnexedParam(request[3], response.data))
            formData?.commodity === EnumCommodity.Electricidad &&
              setIncidentCodeList(mapAnnexedParam(request[4], response.data))
            break
          }
          case EnumGetAnnexedParamsType.SUBCOMPLAINT_TYPE: {
            setSubComplaintTypeList(mapAnnexedParam(paramId!, response.data))
            break
          }
          case EnumGetAnnexedParamsType.UPDATE_OWNER_ADM_TEC_GAS:
          case EnumGetAnnexedParamsType.UPDATE_OWNER_GAS:
          case EnumGetAnnexedParamsType.ADMINISTRATIVE_CHANGE_GAS:
          case EnumGetAnnexedParamsType.ADMINISTRATIVE_TECHNICAL_CHANGE_GAS:
          case EnumGetAnnexedParamsType.TECHNICAL_CHANGE_GAS: {
            const changeReasons = mapAnnexedParam(paramId!, response.data)
            setChangeReasonsListGas(changeReasons)

            const filterValues = getChangeReasonsValuesGas(fetchType)

            const filteredChangeReasons = changeReasons.filter(item =>
              filterValues.includes(item.value as any)
            )

            setFilteredChangeReasonsList(filteredChangeReasons)

            if (fetchType !== EnumGetAnnexedParamsType.TECHNICAL_CHANGE_GAS) {
              setEssentialIndicatorList(mapAnnexedParam(request[1], response.data))
              setProtectedIndicatorList(mapAnnexedParam(request[2], response.data))
            }
            break
          }
          case EnumGetAnnexedParamsType.NEW_WITHOUT_CHANGES: {
            setEssentialIndicatorList(mapAnnexedParam(request[0], response.data)) // request[0] links first paramId with first item in response.data array
            setProtectedIndicatorList(mapAnnexedParam(request[1], response.data))
            break
          }
          case EnumGetAnnexedParamsType.NEW_WITH_CHANGES: {
            setEssentialIndicatorList(mapAnnexedParam(request[0], response.data))
            setProtectedIndicatorList(mapAnnexedParam(request[1], response.data))
            break
          }
          case EnumGetAnnexedParamsType.NEW_SUPPLY: {
            setEssentialIndicatorList(mapAnnexedParam(request[0], response.data))
            setProtectedIndicatorList(mapAnnexedParam(request[1], response.data))
            break
          }
          case EnumGetAnnexedParamsType.UPDATE_OWNER_PWR:
          case EnumGetAnnexedParamsType.ADMINISTRATIVE_CHANGE_PWR:
          case EnumGetAnnexedParamsType.ADMINISTRATIVE_TECHNICAL_CHANGE_PWR: {
            const changeReasons = mapAnnexedParam(request[0], response.data)
            setChangeReasonsListPwr(changeReasons)

            const filteredChangeReasons = changeReasons.filter(item =>
              isChangeOwnership
                ? CHANGE_REASONS_UPDATE_OWNER_PWR.includes(item.value as any)
                : formMode === ProcedureFormMode.CAMBIO_CONTRATO_ATR
                  ? CHANGE_REASONS_ADM_CHANGE_PWR.includes(item.value as any)
                  : CHANGE_REASONS_ADM_CHANGE_PWR_ALL.includes(item.value as any)
            )

            setFilteredChangeReasonsList(filteredChangeReasons)
            setEssentialIndicatorList(mapAnnexedParam(request[1], response.data))
            setbillingFrequencyList(mapAnnexedParam(request[2], response.data))
            break
          }
          case EnumGetAnnexedParamsType.ACTION_GAS: {
            const gasOperationList = mapAnnexedParam(request[0], response.data)

            setSuspensionGasList(gasOperationList)

            break
          }
          default: {
            break
          }
        }
      } else {
        error(errorText)
      }
    })
    setIsFetchingComplaintTypeList(false)
    setIsFetchingComplaintSubTypeList(false)
    setIsFetchingChangeReasonsList(false)
    setIsFetchingEssentialIndicatorList(false)
    setIsFetchingSuspensionGasList(false)

    setIsFetchingAnnexedParameters(false)
  }

  const handleProcedureTypeChange = (type: number) => {
    const action = procedureActionsMap[type]

    if (action) {
      action()
    }
  }

  // this map is used to handle the fetching use case for each procedure type input
  const procedureActionsMap: { [key: number]: () => void } = {
    [EnumProcedureTypeGas.CUPS]: () =>
      getAnnexedParams(formData, EnumGetAnnexedParamsType.COMPLAINT_TYPE),
    [EnumProcedureTypePower.CUPS]: () =>
      getAnnexedParams(formData, EnumGetAnnexedParamsType.COMPLAINT_TYPE),
    [EnumProcedureTypeGas.ADMINISTRATIVE_CHANGE]: () =>
      getAnnexedParams(
        formData,
        EnumGetAnnexedParamsType.ADMINISTRATIVE_CHANGE_GAS,
        AnnexedParameters.MOTIVO_MODIFICACION
      ),
    [EnumProcedureTypeGas.ADMINISTRATIVE_TECHNICAL_CHANGE_GAS]: () =>
      getAnnexedParams(
        formData,
        EnumGetAnnexedParamsType.ADMINISTRATIVE_TECHNICAL_CHANGE_GAS,
        AnnexedParameters.MOTIVO_MODIFICACION
      ),
    [EnumProcedureTypeGas.NEW_CAPACITY]: () =>
      setFormData(prevState => ({
        ...prevState,
        efectiveDate: EnumEffectiveDate.SPECIFIC_DATE
      })),
    [EnumProcedureTypeGas.NEW_WITHOUT_CHANGES]: () =>
      formData.commodity &&
      getAnnexedParams(formData, EnumGetAnnexedParamsType.NEW_WITHOUT_CHANGES),
    [EnumProcedureTypeGas.NEW_WITH_CHANGES]: () =>
      formData.commodity && getAnnexedParams(formData, EnumGetAnnexedParamsType.NEW_WITH_CHANGES),
    [EnumProcedureTypeGas.NEW_SUPPLY]: () =>
      formData.commodity && getAnnexedParams(formData, EnumGetAnnexedParamsType.NEW_SUPPLY),
    [EnumProcedureTypeGas.SUSPEND_SUPPLY_GAS]: () =>
      formData.commodity && getAnnexedParams(formData, EnumGetAnnexedParamsType.ACTION_GAS),
    [EnumProcedureTypeGas.RECONNECT_SUPPLY_GAS]: () =>
      formData.commodity && getAnnexedParams(formData, EnumGetAnnexedParamsType.ACTION_GAS),
    [EnumProcedureTypePower.ADMINISTRATIVE_CHANGE_PWR]: () =>
      getAnnexedParams(formData, EnumGetAnnexedParamsType.ADMINISTRATIVE_CHANGE_PWR),
    [EnumProcedureTypePower.ADMINISTRATIVE_TECHNICAL_CHANGE_PWR]: () =>
      getAnnexedParams(formData, EnumGetAnnexedParamsType.ADMINISTRATIVE_TECHNICAL_CHANGE_PWR)
  }

  const checkValidDates = (beforeDate, afterDate) => {
    if (moment(afterDate).isSameOrAfter(moment(beforeDate))) setInvalidDates(false)
    else setInvalidDates(true)
  }

  const getReasonTerminationDefaultOptions = (reason: 'suspension' | 'termination') => {
    switch (reason) {
      case 'termination':
        return [
          EnumReasonTerminationPower.TERMINATION_ACTIVITY,
          EnumReasonTerminationPower.NON_PAYMENT,
          EnumReasonTerminationPower.EXPIRATION
        ]
      case 'suspension':
        return [EnumReasonTerminationPower.SERVICE_INTERRUPTION]
      default:
        return [
          EnumReasonTerminationPower.TERMINATION_ACTIVITY,
          EnumReasonTerminationPower.NON_PAYMENT,
          EnumReasonTerminationPower.EXPIRATION,
          EnumReasonTerminationPower.SERVICE_INTERRUPTION
        ]
    }
  }

  const powersSection: Section = {
    title: t('procedures:forms.add.titles.powers'),
    inputs: [
      {
        name: 'period1',
        label: t('procedures:forms.add.inputs.period1'),
        type: DataType.NUMBER,
        customDecimalScale: 3,
        suffix: t('procedures:forms.add.units.kW'),
        readOnly: true
      },
      {
        name: 'period2',
        label: t('procedures:forms.add.inputs.period2'),
        type: DataType.NUMBER,
        customDecimalScale: 3,
        suffix: t('procedures:forms.add.units.kW'),
        readOnly: true
      },
      {
        name: 'period3',
        label: t('procedures:forms.add.inputs.period3'),
        type: DataType.NUMBER,
        customDecimalScale: 3,
        suffix: t('procedures:forms.add.units.kW'),
        readOnly: true
      },
      {
        name: 'period4',
        label: t('procedures:forms.add.inputs.period4'),
        type: DataType.NUMBER,
        customDecimalScale: 3,
        suffix: t('procedures:forms.add.units.kW'),
        readOnly: true
      },
      {
        name: 'period5',
        label: t('procedures:forms.add.inputs.period5'),
        type: DataType.NUMBER,
        customDecimalScale: 3,
        suffix: t('procedures:forms.add.units.kW'),
        readOnly: true
      },
      {
        name: 'period6',
        label: t('procedures:forms.add.inputs.period6'),
        type: DataType.NUMBER,
        customDecimalScale: 3,
        suffix: t('procedures:forms.add.units.kW'),
        readOnly: true
      }
    ]
  }

  const sectionsData: Section[] = [
    {
      title: t('procedures:forms.add.titles.generalData'),
      inputs: [
        {
          name: 'contractId',
          label: t('procedures:forms.add.inputs.contract'),
          type: DataType.SELECT,
          isLoading: isFetchingContract || isFetchingContracts,
          selectOptions: contractsOptions,
          defaultValue: contractId,
          required: true,
          readOnly: fixedMode || formMode !== ProcedureFormMode.GENERAL,
          display:
            formMode !== ProcedureFormMode.RECLAMACION_FACTURA &&
            formMode !== ProcedureFormMode.RECLAMACION_TRAMITACION,
          onChange: value => {
            getDetails(+value, cnaeList)
            setFilteredChangeReasonsList([])
          }
        },
        {
          name: 'invoiceCode',
          label: t('procedures:forms.add.inputs.invoiceCode'),
          type: DataType.TEXT,
          readOnly: true,
          display: formMode === ProcedureFormMode.RECLAMACION_FACTURA
        },
        {
          name: 'procedureCode',
          label: t('procedures:forms.add.inputs.procedureCode'),
          type: DataType.TEXT,
          readOnly: true,
          display: formMode === ProcedureFormMode.RECLAMACION_TRAMITACION
        },
        {
          name: 'cupsCode',
          label: t('procedures:forms.add.inputs.cups'),
          type: DataType.TEXT,
          readOnly: true
        },
        {
          name: 'type',
          label: t('procedures:forms.add.inputs.procedureType'),
          type: DataType.SELECT,
          required: true,
          readOnly: invalidContract || fixedMode,
          selectOptions: getProcedureTypeOptions(formData, formMode),
          onChange: type => {
            setFormData(prevState => ({ ...prevState, type: +type }))
            formData.requestType = undefined
            checkCnae()
            if (formData.contractId) {
              handleProcedureTypeChange(+type) // handle fetching use case for each procedure type
            }
          }
        },
        {
          name: 'commodity',
          label: t('procedures:forms.add.inputs.commodity'),
          type: DataType.SELECT,
          selectOptions: enumToSelectOptionArray(EnumCommodity, 'enums:EnumCommodity'),
          readOnly: true
        },
        {
          name: 'toll',
          label: t('procedures:forms.add.inputs.toll'),
          type: DataType.SELECT,
          isLoading: isFetchingTolls,
          selectOptions: listTollItemMapper(tollList ?? []),
          readOnly: true,
          display:
            formMode !== ProcedureFormMode.RECLAMACION_FACTURA &&
            formMode !== ProcedureFormMode.RECLAMACION_TRAMITACION
        },
        {
          name: 'qaEstimated',
          label: t('procedures:forms.add.inputs.qaEstimated'),
          suffix: t('procedures:forms.add.units.kWh'),
          type: DataType.NUMBER,
          readOnly: true,
          display:
            formData?.commodity === EnumCommodity.GasNatural &&
            formMode !== ProcedureFormMode.RECLAMACION_FACTURA &&
            formMode !== ProcedureFormMode.RECLAMACION_TRAMITACION
        },
        {
          name: 'qdHired',
          label: t('procedures:forms.add.inputs.qdHired'),
          suffix: t('procedures:forms.add.units.kWh'),
          type: DataType.NUMBER,
          readOnly: true,
          display:
            formData?.commodity === EnumCommodity.GasNatural &&
            formMode !== ProcedureFormMode.RECLAMACION_FACTURA &&
            formMode !== ProcedureFormMode.RECLAMACION_TRAMITACION
        },
        {
          name: 'cnaeId',
          label: t('procedures:forms.add.inputs.cnae'),
          type: DataType.SELECT,
          isLoading: isFetchingCnae,
          selectOptions: listCnaeItemMapper(cnaeList),
          required:
            formData.type === EnumProcedureType.NEW_SUPPLY ||
            isElectricityUpdateOwner ||
            isCNAERequiredForGas ||
            hasEssentialIndicatorGas,
          readOnly: true,
          clearable: true
        },
        {
          name: 'context',
          label: t('procedures:forms.add.inputs.context'),
          type: DataType.SELECT,
          readOnly: true,
          display: false,
          selectOptions: enumToSelectOptionArray(EnumProcedureProcess, 'enums:EnumProcedureProcess')
        }
      ]
    },
    ...(formData?.commodity === EnumCommodity.Electricidad &&
    formMode !== ProcedureFormMode.RECLAMACION_FACTURA &&
    formMode !== ProcedureFormMode.RECLAMACION_TRAMITACION
      ? [powersSection]
      : []),
    {
      title: t('procedures:forms.add.titles.procedure'),
      inputs: [
        {
          name: 'emptyMsg',
          labelTitle: t('procedures:forms.add.inputs.emptyMsg'),
          type: DataType.LABEL,
          display: !formData.type
        },
        //#region ------------- NEW_CAPACITY OPTIONS -------------//
        {
          name: 'capacityId',
          label: t('procedures:forms.add.inputs.capacityId'),
          type: DataType.SELECT,
          selectOptions: contractCapacities,
          isLoading: isFetchingContract,
          required: formData.type === EnumProcedureTypeGas.NEW_CAPACITY,
          display:
            formData.type === EnumProcedureTypeGas.NEW_CAPACITY ||
            formData.type === EnumProcedureTypeHidden.TECHNICAL_CHANGE,
          readOnly: formData.type === EnumProcedureTypeHidden.TECHNICAL_CHANGE,
          onChange: value => {
            const foundCapacity =
              contractDetails?.capacities && contractDetails?.capacities.find(c => c.id === +value)
            setFormData(prevState => ({
              ...prevState,
              qaEstimated: foundCapacity?.qaEstimada ? foundCapacity?.qaEstimada : 0,
              qdHired: foundCapacity?.qdContratada ? foundCapacity?.qdContratada : 0,
              toll: enumEnumToValueNumber(EnumTipoPeaje, foundCapacity?.toll) ?? 0
            }))
            checkQaIsMissing(foundCapacity)
          }
        },
        //#endregion
        //#region ------------- ADMINISTRATIVE & TECHNICAL CHANGE REASONS -------------//
        {
          name: 'admChangeReasons',
          label: t('procedures:forms.add.inputs.changeReason'),
          type: DataType.SELECT,
          selectOptions: filteredChangeReasonsList,
          isLoading: isFetchingChangeReasonsList,
          readOnly: formData.type === EnumProcedureTypeHidden.TECHNICAL_CHANGE,
          required:
            formData.type === EnumProcedureType.UPDATE_OWNER_ADM_TEC_GAS ||
            formData.type === EnumProcedureType.UPDATE_OWNER_GAS ||
            formData.type === EnumProcedureTypeGas.ADMINISTRATIVE_CHANGE ||
            formData.type === EnumProcedureTypeGas.ADMINISTRATIVE_TECHNICAL_CHANGE_GAS ||
            formData.type === EnumProcedureTypeHidden.TECHNICAL_CHANGE,
          display:
            formData.type === EnumProcedureType.UPDATE_OWNER_ADM_TEC_GAS ||
            formData.type === EnumProcedureType.UPDATE_OWNER_GAS ||
            formData.type === EnumProcedureTypeGas.ADMINISTRATIVE_CHANGE ||
            formData.type === EnumProcedureTypeGas.ADMINISTRATIVE_TECHNICAL_CHANGE_GAS ||
            formData.type === EnumProcedureTypeHidden.TECHNICAL_CHANGE,
          onChange(value) {
            setFormData(prevState => ({ ...prevState, admChangeReasons: value.toString() }))
          }
        },
        {
          name: 'changeReasonPwr',
          label: t('procedures:forms.add.inputs.changeReason'),
          type: DataType.SELECT,
          selectOptions: filteredChangeReasonsList,
          isLoading: isFetchingAnnexedParameters,
          required:
            formData.commodity === EnumCommodity.Electricidad &&
            (isChangeOwnership ||
              formData.type === EnumProcedureType.ADMINISTRATIVE_CHANGE_PWR ||
              formData.type === EnumProcedureType.ADMINISTRATIVE_TECHNICAL_CHANGE_PWR),
          display:
            formData.commodity === EnumCommodity.Electricidad &&
            (isChangeOwnership ||
              formData.type === EnumProcedureType.ADMINISTRATIVE_CHANGE_PWR ||
              formData.type === EnumProcedureType.ADMINISTRATIVE_TECHNICAL_CHANGE_PWR),
          onChange: value => {
            setFormData(prevState => ({ ...prevState, changeReason: value.toString() }))
          }
        },
        //#endregion
        {
          name: 'efectiveDate',
          label: t('procedures:forms.add.inputs.efectiveDate'),
          type: DataType.SELECT,
          selectOptions: enumToSelectOptionArray(
            formData?.commodity === EnumCommodity.GasNatural
              ? EnumEffectiveDate
              : EnumEffectiveDatePower,
            'enums:EnumEfectiveDate'
          ),
          readOnly:
            invalidContract ||
            (capacityNewStartDate && formData.type === EnumProcedureType.NEW_CAPACITY), // capacityNewStartDate is used to identify when a new capacity is created from UpdateContractSupplies API call from contract/details page!
          required:
            formData.type === EnumProcedureType.ADMINISTRATIVE_CHANGE ||
            formData.type === EnumProcedureType.ADMINISTRATIVE_CHANGE_PWR ||
            formData.type === EnumProcedureType.ADMINISTRATIVE_TECHNICAL_CHANGE_PWR ||
            formData.type === EnumProcedureType.ADMINISTRATIVE_TECHNICAL_CHANGE_GAS,
          display:
            formData.type === EnumProcedureType.NEW_WITHOUT_CHANGES ||
            formData.type === EnumProcedureType.NEW_WITH_CHANGES ||
            formData.type === EnumProcedureType.NEW_SUPPLY ||
            formData.type === EnumProcedureType.UPDATE_ATR ||
            formData.type === EnumProcedureTypeHidden.TERMINATION ||
            formData.type === EnumProcedureTypePower.SUSPEND_SUPPLY_PWR ||
            formData.type === EnumProcedureType.NEW_CAPACITY ||
            formData.type === EnumProcedureType.ADMINISTRATIVE_CHANGE ||
            formData.type === EnumProcedureTypeHidden.TECHNICAL_CHANGE ||
            formData.type === EnumProcedureType.ADMINISTRATIVE_CHANGE_PWR ||
            formData.type === EnumProcedureType.ADMINISTRATIVE_TECHNICAL_CHANGE_PWR ||
            formData.type === EnumProcedureType.ADMINISTRATIVE_TECHNICAL_CHANGE_GAS,
          onChange(value, formState) {
            const efectiveDate = +value
            setFormData(prevState => ({
              ...prevState,
              efectiveDate: +value,
              date: undefined,
              newDate: undefined
            }))
          }
        },
        {
          name: 'date',
          label: t('procedures:forms.add.inputs.date'),
          type: DataType.DATE,
          readOnly: invalidContract,
          required: formData.efectiveDate === EnumEffectiveDate.SPECIFIC_DATE,
          display:
            (formData.type === EnumProcedureType.ADMINISTRATIVE_CHANGE_PWR ||
              formData.type === EnumProcedureType.ADMINISTRATIVE_TECHNICAL_CHANGE_PWR ||
              formData.type === EnumProcedureType.NEW_WITHOUT_CHANGES ||
              formData.type === EnumProcedureType.NEW_WITH_CHANGES ||
              formData.type === EnumProcedureType.NEW_SUPPLY ||
              formData.type === EnumProcedureType.UPDATE_ATR ||
              formData.type === EnumProcedureTypeHidden.TERMINATION ||
              formData.type === EnumProcedureTypePower.SUSPEND_SUPPLY_PWR ||
              formData.type === EnumProcedureType.NEW_CAPACITY ||
              formData.type === EnumProcedureType.ADMINISTRATIVE_CHANGE ||
              formData.type === EnumProcedureType.ADMINISTRATIVE_TECHNICAL_CHANGE_GAS ||
              formData.type === EnumProcedureTypeHidden.TECHNICAL_CHANGE) &&
            formData.efectiveDate === EnumEffectiveDate.SPECIFIC_DATE
        },
        {
          name: 'newDate',
          label: t('procedures:forms.add.inputs.newDate'),
          type: DataType.DATE,
          required: formData.efectiveDate === EnumEffectiveDate.SPECIFIC_DATE,
          // block if newDate comes from ChangeOwner form
          readOnly: invalidContract || isChangeOwnership,
          helpText: 'Fecha de inicio del nuevo contrato',
          display: isChangeOwnership && formData.efectiveDate === EnumEffectiveDate.SPECIFIC_DATE
        },
        {
          name: 'essentialIndicatorGas',
          label: t('procedures:forms.add.inputs.essentialIndicatorGas'),
          type: DataType.SELECT,
          isLoading: isFetchingEssentialIndicatorList,
          selectOptions: essentialIndicatorList,
          required:
            formData.type === EnumProcedureType.NEW_SUPPLY ||
            formData.cnaeCode !== undefined ||
            isCNAERequiredForGas,
          clearable: true,
          display:
            formData?.commodity === EnumCommodity.GasNatural &&
            (formData.type === EnumProcedureType.NEW_WITHOUT_CHANGES ||
              formData.type === EnumProcedureType.NEW_WITH_CHANGES ||
              formData.type === EnumProcedureType.NEW_SUPPLY ||
              formData.type === EnumProcedureType.ADMINISTRATIVE_CHANGE ||
              formData.type === EnumProcedureType.ADMINISTRATIVE_TECHNICAL_CHANGE_GAS ||
              formData.type === EnumProcedureType.UPDATE_OWNER_ADM_TEC_GAS ||
              formData.type === EnumProcedureType.UPDATE_OWNER_GAS),
          onChange: (value, formState) => {
            handleEssentialIndicatorGasChange(value?.toString())
          }
        },
        {
          name: 'protectedIndicator',
          label: t('procedures:forms.add.inputs.protectedIndicator'),
          type: DataType.SELECT,
          isLoading: isFetchingEssentialIndicatorList,
          selectOptions: protectedIndicatorList,
          readOnly: true,
          display:
            formData.essentialIndicatorGas !== undefined && formData.essentialIndicatorGas !== null
        },
        {
          name: 'conditionalRegistration',
          label: t('procedures:forms.add.inputs.conditionalRegistration'),
          type: DataType.SWITCH,
          readOnly: invalidContract,
          display:
            formData.type === EnumProcedureType.NEW_WITHOUT_CHANGES ||
            formData.type === EnumProcedureType.NEW_WITH_CHANGES
        },
        {
          name: 'reason',
          label: t('procedures:forms.add.inputs.reason'),
          type: DataType.SELECT,
          selectOptions: getReasonOptionsByPressure(isHighPressure),
          required:
            formData?.commodity === EnumCommodity.GasNatural &&
            formData.type === EnumProcedureType.NEW_WITH_CHANGES,
          readOnly: invalidContract,
          display:
            formData?.commodity === EnumCommodity.GasNatural &&
            formData.type === EnumProcedureType.NEW_WITH_CHANGES,
          onChange(value) {
            setFormData(prevState => ({ ...prevState, reason: +value }))
          }
        },
        // {
        //   name: 'reasonChangeOwner',
        //   label: t('procedures:forms.add.inputs.reasonChangeOwner'),
        //   type: DataType.SELECT,
        //   selectOptions: enumToSelectOptionArray(
        //     formData?.commodity === EnumCommodity.GasNatural
        //       ? EnumProcedureReasonChangeOwnerGas
        //       : EnumProcedureReasonChangeOwnerPower,
        //     'enums:EnumProcedureReasonChangeOwner'
        //   ),
        //   required:
        //     formData.type === EnumProcedureType.UPDATE_OWNER ||
        //     formData.type === EnumProcedureType.UPDATE_OWNER_TECHNICAL,
        //   readOnly: invalidContract,
        //   display:
        //     formData.type === EnumProcedureType.UPDATE_OWNER ||
        //     formData.type === EnumProcedureType.UPDATE_OWNER_TECHNICAL,
        //   onChange(value) {
        //     setFormData(prevState => ({ ...prevState, reasonChangeOwner: +value }))
        //   }
        // },
        {
          name: 'reasonTermination',
          label: t('procedures:forms.add.inputs.reasonTermination'),
          type: DataType.SELECT,
          selectOptions:
            formData?.commodity === EnumCommodity.GasNatural
              ? enumToSelectOptionArray(EnumReasonTerminationGas, 'enums:EnumReasonTermination')
              : formData?.context === EnumProcedureProcess.SUSPENSION_CONTRATO_POWER
                ? enumToSelectOptionArrayDefaultValues(
                    EnumReasonTerminationPower,
                    'enums:EnumReasonTermination',
                    getReasonTerminationDefaultOptions('suspension')
                  )
                : enumToSelectOptionArrayDefaultValues(
                    EnumReasonTerminationPower,
                    'enums:EnumReasonTermination',
                    getReasonTerminationDefaultOptions('termination')
                  ),
          required:
            formData.type === EnumProcedureTypeHidden.TERMINATION ||
            formData.type === EnumProcedureTypePower.SUSPEND_SUPPLY_PWR,
          readOnly:
            invalidContract || formData.context === EnumProcedureProcess.SUSPENSION_CONTRATO_POWER,
          display:
            formData.type === EnumProcedureTypeHidden.TERMINATION ||
            formData.type === EnumProcedureTypePower.SUSPEND_SUPPLY_PWR,
          onChange(value) {
            setFormData(prevState => ({ ...prevState, reasonTermination: +value }))
          }
        },
        {
          name: 'reasonGasSuspension',
          label: t('procedures:forms.add.inputs.reasonGasSuspension'),
          type: DataType.SELECT,
          selectOptions: suspensionGasList,
          isLoading: isFetchingSuspensionGasList,
          required:
            formData.type === EnumProcedureTypeGas.SUSPEND_SUPPLY_GAS ||
            formData.type === EnumProcedureTypeGas.RECONNECT_SUPPLY_GAS,
          display:
            formData.type === EnumProcedureTypeGas.SUSPEND_SUPPLY_GAS ||
            formData.type === EnumProcedureTypeGas.RECONNECT_SUPPLY_GAS,
          readOnly: fixedMode,
          onChange(value) {
            if (value === 'A10002') {
              setIsNonPayment(true)
              if (!hasValidReopeningParameter) {
                error(t('procedures:forms.add.errors.reopeningGasNotValid'))
              }
            } else {
              setIsNonPayment(false)
            }
            setFormData(prevState => ({ ...prevState, reasonGasSuspension: value.toString() }))
          }
        },
        {
          name: 'subrogation',
          label: t('procedures:forms.add.inputs.subrogation'),
          type: DataType.SWITCH,
          readOnly: invalidContract,
          display:
            formData?.commodity === EnumCommodity.GasNatural &&
            GAS_UPDATE_OWNER_VALUES.includes(formData.admChangeReasons as string)
        },
        {
          name: 'principalResidence',
          label: t('procedures:forms.add.inputs.principalResidence'),
          type: DataType.SWITCH,
          readOnly: invalidContract,
          required:
            formData?.commodity === EnumCommodity.GasNatural &&
            GAS_UPDATE_OWNER_VALUES.includes(formData.admChangeReasons as string),
          display:
            formData?.commodity === EnumCommodity.GasNatural &&
            (formData.type === EnumProcedureType.NEW_SUPPLY ||
              GAS_UPDATE_OWNER_VALUES.includes(formData.admChangeReasons as string) ||
              formData.admChangeReasons === '04')
        },
        {
          name: 'gasUsage',
          label: t('procedures:forms.add.inputs.gasUsage'),
          selectOptions: enumToSelectOptionArray(EnumGasUsage, 'enums:EnumGasUsage'),
          type: DataType.SELECT,
          readOnly: invalidContract,
          required:
            formData?.commodity === EnumCommodity.GasNatural &&
            formData.type === EnumProcedureType.NEW_SUPPLY,
          display:
            formData?.commodity === EnumCommodity.GasNatural &&
            formData.type === EnumProcedureType.NEW_SUPPLY,
          onChange(value) {
            setFormData(prevState => ({ ...prevState, gasUsage: +value }))
          }
        },
        {
          name: 'requestedPressure',
          label: t('procedures:forms.add.inputs.requestedPressure'),
          type: DataType.NUMBER,
          readOnly: invalidContract,
          required:
            formData?.commodity === EnumCommodity.GasNatural &&
            formData.type === EnumProcedureType.NEW_SUPPLY &&
            isHighPressure,
          display:
            formData?.commodity === EnumCommodity.GasNatural &&
            formData.type === EnumProcedureType.NEW_SUPPLY &&
            isHighPressure
        },
        {
          name: 'meterProperty',
          label: t('procedures:forms.add.inputs.meterProperty'),
          selectOptions: enumToSelectOptionArray(EnumMeterProperty, 'enums:EnumMeterProperty'),
          type: DataType.SELECT,
          readOnly: invalidContract,
          required:
            formData?.commodity === EnumCommodity.GasNatural &&
            formData.type === EnumProcedureType.NEW_SUPPLY,
          display:
            formData?.commodity === EnumCommodity.GasNatural &&
            formData.type === EnumProcedureType.NEW_SUPPLY
        },
        {
          name: 'deviceTransfer',
          label: t('procedures:forms.add.inputs.deviceTransfer'),
          selectOptions: enumToSelectOptionArray(EnumDeviceTransfer, 'enums:EnumDeviceTransfer'),
          type: DataType.SELECT,
          readOnly: invalidContract,
          required:
            formData?.commodity === EnumCommodity.GasNatural &&
            formData.type === EnumProcedureType.NEW_SUPPLY,
          display:
            formData?.commodity === EnumCommodity.GasNatural &&
            formData.type === EnumProcedureType.NEW_SUPPLY
        },
        {
          name: 'gasStationType',
          label: t('procedures:forms.add.inputs.gasStationType'),
          selectOptions: enumToSelectOptionArray(EnumGasStationType, 'enums:EnumGasStationType'),
          type: DataType.SELECT,
          readOnly: invalidContract,
          required:
            formData.type === EnumProcedureType.NEW_SUPPLY &&
            formData.gasUsage === EnumGasUsage.MOTOR_FUELS,
          display:
            formData.type === EnumProcedureType.NEW_SUPPLY &&
            formData.gasUsage === EnumGasUsage.MOTOR_FUELS
        },
        //#region ------------- POWER OPTIONS -------------//
        {
          name: 'essentialIndicator',
          label: t('procedures:forms.add.inputs.essentialIndicator'),
          type: DataType.SELECT,
          isLoading: isFetchingEssentialIndicatorList,
          selectOptions: essentialIndicatorList,
          display:
            formData?.commodity === EnumCommodity.Electricidad &&
            (isChangeOwnership ||
              formData.type === EnumProcedureType.NEW_WITHOUT_CHANGES ||
              formData.type === EnumProcedureType.NEW_WITH_CHANGES ||
              formData.type === EnumProcedureType.NEW_SUPPLY ||
              formData.type === EnumProcedureType.ADMINISTRATIVE_CHANGE_PWR ||
              formData.type === EnumProcedureType.ADMINISTRATIVE_TECHNICAL_CHANGE_PWR),
          onChange: (value, formState) => {
            setFormData(prevState => ({ ...prevState, essentialIndicator: value.toString() }))
            if (value.toString() !== '01') {
              //esencial por electrodependencia
              setFormData(prevState => ({ ...prevState, essentialMovementDate: undefined }))
            }
          }
        },
        {
          name: 'essentialMovementDate',
          label: t('procedures:forms.add.inputs.essentialMovementDate'),
          type: DataType.DATE,
          required: formData.essentialIndicator === '01',
          display: formData.essentialIndicator === '01'
        },
        {
          name: 'socialSubsidy',
          label: t('procedures:forms.add.inputs.socialSubsidy'),
          type: DataType.SWITCH,
          readOnly: invalidContract,
          display:
            formData?.commodity === EnumCommodity.Electricidad &&
            (formData.type === EnumProcedureType.NEW_WITHOUT_CHANGES ||
              formData.type === EnumProcedureType.NEW_WITH_CHANGES)
        },
        {
          name: 'requestType',
          label: t('procedures:forms.add.inputs.requestType'),
          type: DataType.SELECT,
          selectOptions: enumToSelectOptionArray(EnumRequestType, 'enums:EnumRequestType'),
          readOnly: invalidContract,
          required:
            formData?.commodity === EnumCommodity.Electricidad &&
            formData.type === EnumProcedureType.NEW_WITH_CHANGES,
          display:
            formData?.commodity === EnumCommodity.Electricidad &&
            formData.type === EnumProcedureType.NEW_WITH_CHANGES,
          onChange(value, formState) {
            checkCnae()
            setFormData(prevState => ({ ...prevState, requestType: +value }))
          }
        },
        {
          name: 'administrativeRequest',
          label: t('procedures:forms.add.inputs.administrativeRequest'),
          type: DataType.SELECT,
          selectOptions: enumToSelectOptionArray(
            EnumAdministrativeRequest,
            'enums:EnumAdministrativeRequest'
          ),
          readOnly: invalidContract,
          required:
            formData?.commodity === EnumCommodity.Electricidad &&
            formData.type === EnumProcedureType.NEW_WITH_CHANGES &&
            (formData.requestType === EnumRequestType.ADMINISTRATIVE ||
              formData.requestType === EnumRequestType.BOTH),
          display:
            formData?.commodity === EnumCommodity.Electricidad &&
            formData.type === EnumProcedureType.NEW_WITH_CHANGES &&
            (formData.requestType === EnumRequestType.ADMINISTRATIVE ||
              formData.requestType === EnumRequestType.BOTH)
        },
        {
          name: 'residential',
          label: t('procedures:forms.add.inputs.residential'),
          type: DataType.SELECT,
          selectOptions: enumToSelectOptionArray(EnumResidential, 'enums:EnumResidential'),
          readOnly: invalidContract,
          required:
            (formData?.commodity === EnumCommodity.Electricidad &&
              formData.type === EnumProcedureType.NEW_SUPPLY) ||
            (formData.type === EnumProcedureType.NEW_WITH_CHANGES &&
              (formData.requestType === EnumRequestType.ADMINISTRATIVE ||
                formData.requestType === EnumRequestType.BOTH)),
          display:
            (formData?.commodity === EnumCommodity.Electricidad &&
              (isChangeOwnership ||
                checkPrincipalResidenceForAdministrativeChangePwr ||
                formData.type === EnumProcedureType.NEW_SUPPLY)) ||
            (formData.type === EnumProcedureType.NEW_WITH_CHANGES &&
              (formData.requestType === EnumRequestType.ADMINISTRATIVE ||
                formData.requestType === EnumRequestType.BOTH))
        },
        {
          name: 'tension',
          label: t('procedures:forms.add.inputs.tension'),
          type: DataType.SELECT,
          selectOptions: enumToSelectOptionArray(EnumTension, 'enums:EnumTension'),
          readOnly: invalidContract,
          clearable: true,
          display:
            (formData?.commodity === EnumCommodity.Electricidad &&
              (formData.type === EnumProcedureType.NEW_SUPPLY ||
                formData.type === EnumProcedureType.ADMINISTRATIVE_CHANGE_PWR ||
                formData.type === EnumProcedureType.ADMINISTRATIVE_TECHNICAL_CHANGE_PWR ||
                formData.type === EnumProcedureType.UPDATE_OWNER_ADM_TEC_PWR)) ||
            (formData.type === EnumProcedureType.NEW_WITH_CHANGES &&
              (formData.requestType === EnumRequestType.TECHNICAL ||
                formData.requestType === EnumRequestType.BOTH)) ||
            formData.type === EnumProcedureType.UPDATE_ATR
        },
        {
          name: 'billingFrequency',
          label: t('procedures:forms.add.inputs.billingFrequency'),
          type: DataType.SELECT,
          selectOptions: billingFrequencyList,
          isLoading: isFetchingAnnexedParameters,
          display: formData.changeReason === 'P', // La solicitud implica cambio en la periodicidad de la facturación
          required: formData.changeReason === 'P',
          clearable: true
        },
        //#endregion
        //#region ------------- CUPS/INVOICE/PROCEDURE COMPLAINT OPTIONS -------------//
        {
          name: 'complaintType',
          label: t('procedures:forms.add.inputs.complaintType'),
          type: DataType.SELECT,
          selectOptions: complaintTypeList,
          isLoading: isFetchingComplaintTypeList,
          required:
            formData.type === EnumProcedureTypeGas.CUPS ||
            formData.type === EnumProcedureTypePower.CUPS ||
            formData.type === EnumComplaintTypeInvoice.COMPLAINT_INVOICE_GAS ||
            formData.type === EnumComplaintTypeInvoice.COMPLAINT_INVOICE_PWR ||
            formData.type === EnumComplaintTypeProcedure.COMPLAINT_PROCEDURE_GAS ||
            formData.type === EnumComplaintTypeProcedure.COMPLAINT_PROCEDURE_PWR,
          display:
            formData.type === EnumProcedureTypeGas.CUPS ||
            formData.type === EnumProcedureTypePower.CUPS ||
            formData.type === EnumComplaintTypeInvoice.COMPLAINT_INVOICE_GAS ||
            formData.type === EnumComplaintTypeInvoice.COMPLAINT_INVOICE_PWR ||
            formData.type === EnumComplaintTypeProcedure.COMPLAINT_PROCEDURE_GAS ||
            formData.type === EnumComplaintTypeProcedure.COMPLAINT_PROCEDURE_PWR,
          onChange(value) {
            setFormData(prevState => ({ ...prevState, complaintSubType: undefined }))
            const paramId = getComplaintSubTypeByCategory(
              value.toString(),
              formData.type!,
              formData.commodity!
            )
            getAnnexedParams(formData, EnumGetAnnexedParamsType.SUBCOMPLAINT_TYPE, paramId)
          }
        },
        {
          name: 'complaintSubType',
          label: t('procedures:forms.add.inputs.complaintSubType'),
          type: DataType.SELECT,
          selectOptions: subComplaintTypeList,
          isLoading: isFetchingComplaintSubTypeList,
          required:
            formData.type === EnumProcedureTypeGas.CUPS ||
            formData.type === EnumProcedureTypePower.CUPS ||
            formData.type === EnumComplaintTypeInvoice.COMPLAINT_INVOICE_GAS ||
            formData.type === EnumComplaintTypeInvoice.COMPLAINT_INVOICE_PWR ||
            formData.type === EnumComplaintTypeProcedure.COMPLAINT_PROCEDURE_GAS ||
            formData.type === EnumComplaintTypeProcedure.COMPLAINT_PROCEDURE_PWR,
          display:
            formData.type === EnumProcedureTypeGas.CUPS ||
            formData.type === EnumProcedureTypePower.CUPS ||
            formData.type === EnumComplaintTypeInvoice.COMPLAINT_INVOICE_GAS ||
            formData.type === EnumComplaintTypeInvoice.COMPLAINT_INVOICE_PWR ||
            formData.type === EnumComplaintTypeProcedure.COMPLAINT_PROCEDURE_GAS ||
            formData.type === EnumComplaintTypeProcedure.COMPLAINT_PROCEDURE_PWR,
          onChange(value) {
            setFormData(prevState => ({ ...prevState, complaintSubType: value.toString() }))
          }
        },
        {
          name: 'contractConcept',
          label: t('procedures:forms.add.inputs.contractConcept'),
          type: DataType.SELECT,
          selectOptions: contractConceptsList,
          required: formData.complaintSubType === '066' || formData.complaintSubType === '034',
          display: formData.complaintSubType === '066' || formData.complaintSubType === '034'
        },
        {
          name: 'invoiceConcept',
          label: t('procedures:forms.add.inputs.invoiceConcept'),
          type: DataType.SELECT,
          selectOptions: invoiceConceptsList,
          required: formData.complaintSubType === '008',
          display: formData.complaintSubType === '008'
        },
        {
          name: 'incidentCode',
          label: t('procedures:forms.add.inputs.incidentCode'),
          type: DataType.SELECT,
          selectOptions: incidentCodeList,
          required: formData.complaintSubType === '003',
          display:
            formData.complaintSubType === '003' && formData.type === EnumProcedureTypePower.CUPS
        },
        {
          name: 'complaintOwnerType',
          label: t('procedures:forms.add.inputs.complaintOwnerType'),
          type: DataType.SELECT,
          selectOptions: complaintOwnerTypeList,
          isLoading: isFetchingComplaintTypeList,
          required:
            formData.type === EnumProcedureTypeGas.CUPS ||
            formData.type === EnumProcedureTypePower.CUPS ||
            formData.type === EnumComplaintTypeInvoice.COMPLAINT_INVOICE_GAS ||
            formData.type === EnumComplaintTypeInvoice.COMPLAINT_INVOICE_PWR ||
            formData.type === EnumComplaintTypeProcedure.COMPLAINT_PROCEDURE_GAS ||
            formData.type === EnumComplaintTypeProcedure.COMPLAINT_PROCEDURE_PWR,
          display:
            formData.type === EnumProcedureTypeGas.CUPS ||
            formData.type === EnumProcedureTypePower.CUPS ||
            formData.type === EnumComplaintTypeInvoice.COMPLAINT_INVOICE_GAS ||
            formData.type === EnumComplaintTypeInvoice.COMPLAINT_INVOICE_PWR ||
            formData.type === EnumComplaintTypeProcedure.COMPLAINT_PROCEDURE_GAS ||
            formData.type === EnumComplaintTypeProcedure.COMPLAINT_PROCEDURE_PWR
        },
        {
          name: 'startDate',
          label: t('procedures:forms.add.inputs.startDate'),
          type: DataType.DATE,
          required: formData.complaintSubType === '049',
          display: formData.complaintSubType === '049',
          onChange: (value: any) => {
            value &&
              setFormData(prevState => ({
                ...prevState,
                startDate: value
              }))
            if (formData.endDate) checkValidDates(value, formData.endDate)
          },
          errorState: invalidDates,
          errorText: invalidDates ? t('procedures:forms.add.errors.startDate') : ''
        },
        {
          name: 'endDate',
          label: t('procedures:forms.add.inputs.endDate'),
          type: DataType.DATE,
          required: formData.complaintSubType === '049',
          display: formData.complaintSubType === '049',
          onChange: (value: any) => {
            value &&
              setFormData(prevState => ({
                ...prevState,
                endDate: value
              }))
            if (formData.startDate) checkValidDates(formData.startDate, value)
          },
          errorState: invalidDates,
          errorText: invalidDates ? t('procedures:forms.add.errors.endDate') : ''
        },
        {
          name: 'deadline',
          label: t('procedures:forms.add.inputs.deadline'),
          type: DataType.DATE,
          onChange: value => {
            if (value !== null && value !== undefined && !moment(value.toString()).isValid())
              setInvalidDates(true)
          },
          display:
            formData.type === EnumProcedureTypeGas.CUPS ||
            formData.type === EnumProcedureTypePower.CUPS ||
            formData.type === EnumComplaintTypeInvoice.COMPLAINT_INVOICE_GAS ||
            formData.type === EnumComplaintTypeInvoice.COMPLAINT_INVOICE_PWR ||
            formData.type === EnumComplaintTypeProcedure.COMPLAINT_PROCEDURE_GAS ||
            formData.type === EnumComplaintTypeProcedure.COMPLAINT_PROCEDURE_PWR
        },
        {
          name: 'priority',
          label: t('procedures:forms.add.inputs.priority'),
          type: DataType.SWITCH,
          display:
            formData.type === EnumProcedureTypeGas.CUPS ||
            formData.type === EnumProcedureTypePower.CUPS ||
            formData.type === EnumComplaintTypeInvoice.COMPLAINT_INVOICE_GAS ||
            formData.type === EnumComplaintTypeInvoice.COMPLAINT_INVOICE_PWR ||
            formData.type === EnumComplaintTypeProcedure.COMPLAINT_PROCEDURE_GAS ||
            formData.type === EnumComplaintTypeProcedure.COMPLAINT_PROCEDURE_PWR
        },
        //#endregion
        {
          name: 'comments',
          label: t('procedures:forms.add.inputs.comments'),
          type: DataType.TEXT,
          display:
            isChangeOwnership ||
            formData.type === EnumProcedureType.NEW_WITHOUT_CHANGES ||
            formData.type === EnumProcedureType.NEW_WITH_CHANGES ||
            formData.type === EnumProcedureType.NEW_SUPPLY ||
            formData.type === EnumProcedureTypeHidden.TERMINATION ||
            formData.type === EnumProcedureTypeGas.CUPS ||
            formData.type === EnumProcedureTypePower.CUPS ||
            formData.type === EnumComplaintTypeInvoice.COMPLAINT_INVOICE_GAS ||
            formData.type === EnumComplaintTypeInvoice.COMPLAINT_INVOICE_PWR ||
            formData.type === EnumComplaintTypeProcedure.COMPLAINT_PROCEDURE_GAS ||
            formData.type === EnumComplaintTypeProcedure.COMPLAINT_PROCEDURE_PWR ||
            formData.type === EnumProcedureTypeGas.NEW_CAPACITY ||
            formData.type === EnumProcedureTypeGas.ADMINISTRATIVE_CHANGE ||
            formData.type === EnumProcedureTypeGas.ADMINISTRATIVE_TECHNICAL_CHANGE_GAS ||
            formData.type === EnumProcedureTypeHidden.TECHNICAL_CHANGE ||
            formData.type === EnumProcedureType.UPDATE_ATR ||
            formData.type === EnumProcedureType.SUSPEND_SUPPLY_GAS ||
            formData.type === EnumProcedureType.RECONNECT_SUPPLY_GAS ||
            formData.type === EnumProcedureType.SUSPEND_SUPPLY_PWR ||
            formData.type === EnumProcedureType.ADMINISTRATIVE_CHANGE_PWR ||
            formData.type === EnumProcedureType.ADMINISTRATIVE_TECHNICAL_CHANGE_PWR,
          maxLength: MAX_CHAR_COMMENTS,
          helpText: `${remainingChars}/${maxChar}`,
          onChange: (value, formState) => {
            setRemainingChars(maxChar - value.toString().length)
            setFormData(formState as ProcedureFormData)
          }
        }
      ]
    }
  ]

  const options: FormOptions = {
    columns: 3,
    analytics: {
      customer: getCustomerDomain(),
      view: t('procedures:forms.add.analytics')
    },
    onSave: data => onSaveProcedure(data),
    saveDisabled:
      invalidContract ||
      missingCNAE ||
      invalidDates ||
      (isNonPayment && !hasValidReopeningParameter)
  }

  const onSaveProcedure = async (data: ProcedureFormData) => {
    // Cancel Button
    if (!data) {
      if (isChangeOwnership) {
        await getConfirmation(
          ConfirmationDialogs.CANCEL_PROCEDURE_CHANGE_OWNERSHIP,
          undefined,
          true
        ).then(
          async () => {
            closeConfirmation()

            //Aquí llamar al metodo para eliminar el cambio de titular ya creado
            await revertChangeOwner(contractId!, oldContractId!).then(response => {
              if (response.success) {
                info(t('contracts:notifications.create.success'))

                fetchClientContractsByID(clientData?.id)
                history.push(NonAuthRoutes.ClientFile, { clientID: clientData?.id })
              } else {
                error(t('contracts:notifications.create.error'))
              }
            })
          },
          () => {
            return false
          }
        )

        return false
      }

      history.goBack()
      return false
    }

    setIsSaving(true)

    if (missingFieldsFormWithRequiredText(sectionsData, data)) {
      setIsSaving(false)
      error(t('procedures:forms.add.actions.save.errors.incompleteForm'))
      return false
    }

    let newProcedure: NewProcedure | null = null
    newProcedure = getParameters(data)

    const proceduresRequireContact = [
      {
        type: ProcedureTypes.Baja_Suministro_PWR,
        parameters: { type: 63, value: '01' }
      },
      {
        type: ProcedureTypes.Alta_Nuevo_Suministro
      }
    ]

    const contactPrincipal = clientData?.contactInfo?.find(c => c.principal)

    const requiresContact = proceduresRequireContact.some(
      rule =>
        rule.type == newProcedure?.type &&
        (!rule.parameters ||
          newProcedure?.parameters?.some(
            p => p.type == rule.parameters!.type && p.value == rule.parameters!.value
          ))
    )

    if (requiresContact && !(contactPrincipal && contactPrincipal.phoneNumber)) {
      setIsSaving(false)
      error(t('procedures:forms.add.actions.save.errors.missingContact'))
      return false
    }

if (
  formData.cnaeCode === undefined &&
  (formData.requestType === EnumRequestType.ADMINISTRATIVE ||
    formData.requestType === EnumRequestType.BOTH)
) {
  const confirmed = await getConfirmation(
    ConfirmationDialogs.CONFIRM_PROCEDURE_CNAE,
    undefined,
    true
  ).then(
    () => true,
    () => false
  )

  closeConfirmation()

  if (!confirmed) {
    setIsSaving(false)
    return false
  }
}

    const attachments = await mapAttachedDocumentFilesToDocuments(attachedDocumentFiles)
    if (newProcedure && attachments) {
      return await createProcedure({
        ...newProcedure,
        documents: attachments,
        client: clientData?.description,
        clientData: { id: clientData?.id } as SubjectModel
      }).then(async response => {
        setIsSaving(false)
        if (response.success) {
          info(t('procedures:forms.add.actions.save.success'))

          if (isChangeOwnership) {
            fetchClientContractsByID(clientData?.id)
          }

          history.push(NonAuthRoutes.ClientFile, {
            clientID: clientData?.id
          })
          return true
        } else {
          response.error
            ? showErrorDetailListDialog(
                t('procedures:forms.add.actions.save.errors.api'),
                [response.error],
                moment(newProcedure?.date).format('DD/MM/yyyy')
              )
            : notifyError(t('httpErrors:api.subtitle'))
          return false
        }
      })
    } else {
      return false
    }
  }

  const handleCloseDialog = () => {
    onResetDialog()
    setShowAttachmentDialog(false)
  }

  const handleOpenDialog = () => {
    setShowAttachmentDialog(true)
  }

  const handleAddDocument = (document: AttachedDocumentFile) => {
    setAttachedDocumentFiles([
      ...attachedDocumentFiles,
      { ...document, id: generateUniqueKeyWithNoPrefix() }
    ])
    handleCloseDialog()
  }

  const handleAddAndNewDocument = (document: AttachedDocumentFile) => {
    handleAddDocument(document)

    // onResetDialog resetea los states del Dialog pero el Dropdown requiere unmount del componente.
    // Por lo que haremos close + open. Es necesario darle un timeout al open ya que el close es async.
    setTimeout(() => handleOpenDialog(), 500)
  }

  const handleDeleteDocument = (document: AttachedDocumentFile) => {
    setAttachedDocumentFiles(attachedDocumentFiles.filter(doc => doc.id !== document.id))
  }

  const handleEssentialIndicatorGasChange = (value: string) => {
    switch (value) {
      case '0':
        setFormData(prevState => ({ ...prevState, protectedIndicator: '0' })) //No protegido
        break
      case '1':
      case '3':
      case '4':
      case '5':
      case '6':
      case '7':
        setFormData(prevState => ({ ...prevState, protectedIndicator: '2' })) //Protegido mecanismo solidaridad
        break
      case '2':
        setFormData(prevState => ({ ...prevState, protectedIndicator: '1' })) //Protegido
        break
      default:
        setFormData(prevState => ({ ...prevState, protectedIndicator: undefined }))
    }
  }

  return (
    <Page notification={globalNotification} fullWidth={false} layout={PageLayout.SINGLE_COLUMN}>
      <PageHeader title={t('procedures:title')} />
      <Box mb={1}>
        <Box mb={1}>
          <Breadcrumbs separator=">" aria-label="breadcrumb">
            {clientData && (
              <BreadcrumbLink toPathName="/client/file" state={{ clientID: clientData?.id }}>
                {`Cliente ${getLinkClientText(clientData)} - ID ${clientData.documentID}`}
              </BreadcrumbLink>
            )}
            {contractDetails && (
              <BreadcrumbLink toPathName="/client/file" state={{ clientID: clientData?.id }}>
                {`Contrato ${contractDetails.contractCode}`}
              </BreadcrumbLink>
            )}
          </Breadcrumbs>
        </Box>
        <Paper elevation={6}>
          <Form
            title={t('procedures:forms.add.titles.main')}
            sections={sectionsData}
            data={formData}
            options={options}
          />
        </Paper>
        <Paper elevation={6}></Paper>
      </Box>
      <AttachedFilesTable
        files={attachedDocumentFiles}
        documentsTypes={documentsTypes}
        onClickAddButton={handleOpenDialog}
        onClickDeleteButton={handleDeleteDocument}
      />
      {(contractDetails?.commodity || formData?.commodity) && (
        <AttachFileDialog
          ref={attachFileDialogRef}
          open={showAttachmentDialog}
          documentTypes={documentsTypes}
          onClose={handleCloseDialog}
          onSave={handleAddDocument}
          onSaveAndNew={handleAddAndNewDocument}
        />
      )}

      <ErrorDetailList {...errorDetailListDialogProps}></ErrorDetailList>
    </Page>
  )
}
